import { useEffect, useState, useMemo } from "react";
import api from "@/api";

const STATUS_META = {
  implemented:  { label: "Implementado", color: "#009639", icon: "fa-circle-check" },
  configurable: { label: "Configurable",  color: "#0033AD", icon: "fa-sliders" },
  roadmap:      { label: "Roadmap Fase 2", color: "#D4A437", icon: "fa-route" },
  needs_lala:   { label: "Requiere definición LALA", color: "#E4002B", icon: "fa-circle-exclamation" },
};

const DOMAIN_META = {
  ciberseguridad: { label: "Ciberseguridad", icon: "fa-shield-halved" },
  conectividad:   { label: "Conectividad",   icon: "fa-network-wired" },
  arquitectura:   { label: "Arquitectura empresarial", icon: "fa-cubes" },
  devops:         { label: "DevOps",          icon: "fa-gears" },
};

const OWNER_META = {
  qbase:   { label: "QBase",          color: "#0033AD" },
  cris:    { label: "Cris",           color: "#7B3FBF" },
  lala:    { label: "LALA",           color: "#E4002B" },
  tecnico: { label: "Equipo técnico", color: "#4F537A" },
};

export default function ArchitectureReview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ domain: "", status: "", owner: "", q: "" });
  const [openId, setOpenId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/architecture-review", { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filters.domain, filters.status, filters.owner]);

  const filtered = useMemo(() => {
    if (!data?.items) return [];
    if (!filters.q) return data.items;
    const ql = filters.q.toLowerCase();
    return data.items.filter(it =>
      it.question.toLowerCase().includes(ql) ||
      it.answer.toLowerCase().includes(ql) ||
      it.id.toLowerCase().includes(ql)
    );
  }, [data, filters.q]);

  const downloadFile = async (path, suggested) => {
    const res = await api.get(path, { responseType: "blob" });
    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = suggested;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="text-center py-20 font-hand text-2xl text-[var(--lala-blue)]">Cargando Architecture Review…</div>;
  }
  if (!data) return null;

  const summary = data.by_status || {};
  const total = data.total || 0;
  const readyPct = Math.round(100 * ((summary.implemented || 0) + (summary.configurable || 0)) / Math.max(total, 1));

  return (
    <div className="px-6 py-10 max-w-7xl mx-auto" data-testid="architecture-review-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-hand text-[var(--lala-blue)] text-2xl mb-1">architecture review · enviado por LALA</p>
          <h1 className="text-4xl font-extrabold text-[var(--ink)]">Respuesta a los {total} criterios técnicos</h1>
          <p className="text-[var(--ink-soft)] mt-2 max-w-3xl">
            Cuestionario oficial recibido de Grupo LALA con respuesta recomendada, evidencia interna,
            riesgo y acción requerida por criterio. Trazabilidad 1 a 1 con la hoja Excel original.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="ar-download-pdf"
            onClick={() => downloadFile("/architecture-review/pdf", "lala-elite-architecture-review.pdf")}
            className="px-5 py-2.5 rounded-full bg-[var(--lala-blue)] text-white font-semibold hover:bg-[var(--lala-blue-deep)] transition flex items-center gap-2"
          >
            <i className="fa-solid fa-file-pdf"></i> Descargar PDF
          </button>
          <button
            data-testid="ar-download-xlsx"
            onClick={() => downloadFile("/architecture-review/export.xlsx", "lala-elite-architecture-review.xlsx")}
            className="px-5 py-2.5 rounded-full bg-white border-2 border-[var(--lala-blue)] text-[var(--lala-blue)] font-semibold hover:bg-[var(--cream)] transition flex items-center gap-2"
          >
            <i className="fa-solid fa-file-excel"></i> XLSX
          </button>
          <button
            data-testid="ar-download-csv"
            onClick={() => downloadFile("/architecture-review/export.csv", "lala-elite-architecture-review.csv")}
            className="px-5 py-2.5 rounded-full bg-white border-2 border-[var(--lala-blue)] text-[var(--lala-blue)] font-semibold hover:bg-[var(--cream)] transition flex items-center gap-2"
          >
            <i className="fa-solid fa-file-csv"></i> CSV
          </button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-8">
        <div className="p-4 rounded-2xl text-white" style={{ background: "var(--lala-blue-deep)" }}>
          <p className="text-xs uppercase font-bold opacity-80">Readiness preliminar</p>
          <p className="text-3xl font-extrabold mt-1">~{readyPct}%</p>
        </div>
        {["implemented", "configurable", "roadmap", "needs_lala"].map((k) => {
          const sm = STATUS_META[k];
          return (
            <div key={k} className="p-4 rounded-2xl border-2" style={{ borderColor: sm.color, background: "white" }}>
              <p className="text-xs uppercase font-bold" style={{ color: sm.color }}>{sm.label}</p>
              <p className="text-3xl font-extrabold mt-1 text-[var(--ink)]">{summary[k] || 0}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white/70 rounded-2xl p-4 mb-6 flex gap-3 flex-wrap items-end border border-[var(--lala-blue)]/10">
        <div>
          <label className="text-xs font-semibold text-[var(--ink-soft)] uppercase block mb-1">Dominio</label>
          <select data-testid="ar-filter-domain" value={filters.domain}
            onChange={(e) => setFilters({ ...filters, domain: e.target.value })}
            className="px-4 py-2 rounded-lg border border-[var(--lala-blue)]/30 bg-white min-w-[180px]">
            <option value="">Todos</option>
            {Object.entries(DOMAIN_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-[var(--ink-soft)] uppercase block mb-1">Estado</label>
          <select data-testid="ar-filter-status" value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 rounded-lg border border-[var(--lala-blue)]/30 bg-white min-w-[180px]">
            <option value="">Todos</option>
            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-[var(--ink-soft)] uppercase block mb-1">Owner</label>
          <select data-testid="ar-filter-owner" value={filters.owner}
            onChange={(e) => setFilters({ ...filters, owner: e.target.value })}
            className="px-4 py-2 rounded-lg border border-[var(--lala-blue)]/30 bg-white min-w-[140px]">
            <option value="">Todos</option>
            {Object.entries(OWNER_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-semibold text-[var(--ink-soft)] uppercase block mb-1">Búsqueda</label>
          <input data-testid="ar-search" type="text" placeholder="ID, pregunta o respuesta..."
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="w-full px-4 py-2 rounded-lg border border-[var(--lala-blue)]/30 bg-white" />
        </div>
        <button
          onClick={() => setFilters({ domain: "", status: "", owner: "", q: "" })}
          className="px-4 py-2 text-sm text-[var(--ink-soft)] hover:text-[var(--lala-blue)] font-semibold"
        >
          Limpiar
        </button>
      </div>

      <p className="text-sm text-[var(--ink-soft)] mb-3">
        Mostrando <b className="text-[var(--lala-blue)]">{filtered.length}</b> de <b>{total}</b> criterios.
      </p>

      {/* Cards list */}
      <div className="space-y-3">
        {filtered.map((it) => {
          const sm = STATUS_META[it.status];
          const dm = DOMAIN_META[it.domain] || { label: it.domain, icon: "fa-circle" };
          const om = OWNER_META[it.owner] || { label: it.owner, color: "#4F537A" };
          const open = openId === it.id;
          return (
            <div
              key={it.id}
              data-testid={`ar-item-${it.id}`}
              className="bg-white rounded-2xl border border-[var(--lala-blue)]/10 overflow-hidden"
            >
              <button
                onClick={() => setOpenId(open ? null : it.id)}
                className="w-full text-left p-4 hover:bg-[var(--cream)]/40 transition"
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <span className="text-xs font-mono font-bold text-[var(--lala-blue)] bg-[var(--cream)] px-2 py-1 rounded">
                    {it.id}
                  </span>
                  <span className="text-xs uppercase font-bold text-[var(--ink-soft)] flex items-center gap-1">
                    <i className={`fa-solid ${dm.icon}`}></i> {dm.label}
                  </span>
                  <span className="text-xs text-[var(--ink-soft)]">·</span>
                  <span className="text-xs text-[var(--ink-soft)]">{it.section}</span>
                  <div className="ml-auto flex gap-2 items-center">
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full text-white"
                      style={{ background: sm.color }}>
                      {sm.label}
                    </span>
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full text-white"
                      style={{ background: om.color }}>
                      {om.label}
                    </span>
                    <i className={`fa-solid ${open ? "fa-chevron-up" : "fa-chevron-down"} text-[var(--ink-soft)]`}></i>
                  </div>
                </div>
                <p className="mt-2 text-[var(--ink)] font-semibold">{it.question}</p>
              </button>
              {open && (
                <div className="px-5 pb-5 pt-1 border-t border-[var(--lala-blue)]/5">
                  <div className="space-y-3 mt-2">
                    <Field label="Respuesta recomendada" value={it.answer} />
                    <Field label="Evidencia interna" value={it.evidence} mono />
                    {it.risk && it.risk !== "—" && (
                      <Field label="Riesgo si no se cierra" value={it.risk} tone="red" />
                    )}
                    {it.action && it.action !== "—" && (
                      <Field label="Acción requerida" value={it.action} />
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom narrative */}
      <div className="mt-12 p-6 rounded-2xl bg-[var(--lala-blue-deep)] text-white">
        <h2 className="text-2xl font-bold mb-2">Próximos pasos sugeridos</h2>
        <ol className="space-y-2 text-sm opacity-90 list-decimal pl-5">
          <li>Sesión técnica con TI/Arquitectura de LALA para validar este reporte criterio por criterio.</li>
          <li>Cierre de definiciones LALA: URLs SAP, IP whitelist o PrivateLink, RPO/RTO formales, retención de logs.</li>
          <li>Firma de Anexo Técnico con SLAs, ventanas de mantenimiento, soporte, plan de incidentes.</li>
          <li>Pre-go-live: pentest externo, simulacro DR, validación SOC 2 Type 1 en curso.</li>
          <li>Go-live monitoreado con seguimiento semanal durante el primer mes.</li>
        </ol>
        <p className="text-xs mt-4 opacity-75">
          Este documento es una autoevaluación técnica. No constituye declaración de cumplimiento formal ni certificación.
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, tone, mono }) {
  const colorMap = { red: "text-red-700", blue: "text-[var(--lala-blue)]" };
  return (
    <div>
      <p className="text-xs uppercase font-bold text-[var(--ink-soft)] mb-1">{label}</p>
      <p className={`${mono ? "font-mono text-xs" : "text-sm"} ${tone ? colorMap[tone] : "text-[var(--ink)]"} leading-relaxed`}>
        {value}
      </p>
    </div>
  );
}
